"use client";

import { SiteTemplatePageSectionTypeOfferDefaultListItemSettingModel } from "@/Apis/ClinicApi";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { OfferSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeFifteenOfferSectionGenerated, {
  HomeFifteenOfferSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenOfferSection.generated";
import HomeThirteenOfferSectionGenerated, {
  HomeThirteenOfferSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenOfferSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedOfferRenderer = (props: OfferSectionProps) => ReactElement;

const OfferAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateOfferAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: OfferSectionProps,
): SiteTemplatePageSectionTypeOfferDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: OfferSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: OfferSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getItemImageUrl(
  item?: SiteTemplatePageSectionTypeOfferDefaultListItemSettingModel | null,
): string | undefined {
  return item?.Image?.Url ?? undefined;
}

function getItemImageAlt(
  item: SiteTemplatePageSectionTypeOfferDefaultListItemSettingModel | undefined,
  fallback: string,
): string {
  return item?.Image?.AltText ?? item?.Title ?? fallback;
}

const generatedOfferRenderers: Record<string, GeneratedOfferRenderer> = {
  [OfferAppearance.Home13.key]: (props) => (
    <HomeThirteenOfferSectionGenerated
      {...buildGeneratedSectionProps<HomeThirteenOfferSectionGeneratedProps>(
        "HomeThirteenOfferSection",
        {
          offerSection4666s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              [
                "offers-item-thirteen wow fadeInUp",
                "offers-item-thirteen two wow fadeInUp",
                "offers-item-thirteen three wow fadeInUp",
              ][index] ?? "offers-item-thirteen wow fadeInUp",
            dataWowDuration: `${index + 1}s`,
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            src:
              getItemImageUrl(item) ??
              "/Templates/Doccure/img/deals/offer-img-1.png",
            imageWithBasePath1Src:
              getItemImageUrl(item) ??
              "/Templates/Doccure/img/deals/shadow-img-1.png",
            alt: getItemImageAlt(item, "offer"),
            imageWithBasePath1Alt: getItemImageAlt(item, "offer"),
          })),
        },
      )}
    />
  ),
  [OfferAppearance.Home15.key]: (props) => (
    <HomeFifteenOfferSectionGenerated
      {...buildGeneratedSectionProps<HomeFifteenOfferSectionGeneratedProps>(
        "HomeFifteenOfferSection",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text2: "",
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div0Div0Div0P0PText2: "",
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(props, "offer"),
          offerSection12126s: getListItems(props).map((item, index) => ({
            key: item.Id,
            src:
              getItemImageUrl(item) ??
              "/Templates/Doccure/img/category/offer-img-1.jpg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            dataWowDuration: `${index + 2}s`,
            href: item.Link ?? "#",
            alt: getItemImageAlt(item, "offer"),
            imageWithBasePath0Alt: "flower",
            imageWithBasePath0Src:
              "/Templates/Doccure/img/icons/flower-icon.svg",
          })),
        },
      )}
    />
  ),
};

const GeneratedOfferSection: FC<OfferSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedOfferRenderers,
    OfferAppearance.Home13.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedOfferSection;
